import { lazy, type ComponentType } from 'react';
import { logger } from './logger';

// TBT optimization utilities
export const createLazyComponent = <T extends ComponentType<Record<string, unknown>>>(
  importFunction: () => Promise<{ default: T }>,
  delay: number = 0,
) => {
  return lazy(() => {
    return new Promise<{ default: T }>((resolve) => {
      setTimeout(() => {
        importFunction().then(resolve);
      }, delay);
    });
  });
};

// Defer heavy library loading
export const deferredImports = {
  lottie: () =>
    new Promise<typeof import('lottie-react')>((resolve) => {
      setTimeout(async () => {
        const module = await import('lottie-react');
        resolve(module);
      }, 100);
    }),

  colorThief: () =>
    new Promise<typeof import('colorthief')>((resolve) => {
      // Use requestIdleCallback for better performance
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          async () => {
            const module = await import('colorthief');
            resolve(module);
          },
          { timeout: 1000 },
        );
      } else {
        setTimeout(async () => {
          const module = await import('colorthief');
          resolve(module);
        }, 100); // Reduced delay for better UX
      }
    }),

  qrCode: () =>
    new Promise<typeof import('qrcode.react')>((resolve) => {
      setTimeout(async () => {
        const module = await import('qrcode.react');
        resolve(module);
      }, 150);
    }),

  html2canvas: () =>
    new Promise<unknown>((resolve) => {
      // Use requestIdleCallback for better performance and defer loading
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          async () => {
            const module = await import('html2canvas');
            resolve(module.default);
          },
          { timeout: 2000 }, // Increased timeout for non-critical usage
        );
      } else {
        // Increased delay for better TBT optimization
        setTimeout(async () => {
          const module = await import('html2canvas');
          resolve(module.default);
        }, 500);
      }
    }),
};

// Resource prioritization
export const prioritizeResources = () => {
  // Critical resources (load immediately)
  const criticalResources = ['react', 'react-dom', 'react-router-dom'];

  // Non-critical resources (defer loading)
  const nonCriticalResources = ['lottie-react', 'colorthief', 'qrcode.react', 'html2canvas'];

  return { criticalResources, nonCriticalResources };
};

// Intersection Observer for lazy loading
export const createIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options: IntersectionObserverInit = {},
) => {
  const defaultOptions = {
    rootMargin: '50px',
    threshold: 0.1,
    ...options,
  };

  return new IntersectionObserver(callback, defaultOptions);
};

// Performance monitoring
export const measureTBT = () => {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.entryType === 'measure') {
          logger.log(`TBT Measurement: ${entry.name} - ${entry.duration}ms`);
        }
      });
    });

    observer.observe({ entryTypes: ['measure'] });
  }
};
